import { AyurmaStrings } from "../../../i18n/ayurma";
import { SvgConverterType } from "../../../reports/Report";
import { ReportLang } from "../../../i18n/reportLang";
import { formatAmount, parseAmount } from "../../../whiteLabel/format";
import { useRasterizedBlock } from "../../../whiteLabel/useRasterizedBlock";
import FlatStatCard from "../components/FlatStatCard";
import LabeledFillRows from "../components/LabeledFillRows";
import MacroTriangle, { MacroNode } from "../components/MacroTriangle";
import WhiteLabelCard from "../components/WhiteLabelCard";
import WhiteLabelPage from "../WhiteLabelPage/WhiteLabelPage";
import { WhiteLabelPageProps } from "../types";

import classes from "./CaloricNutritionPage.module.css";

type Props = WhiteLabelPageProps & {
  svgConverter: SvgConverterType;
  isCreatingPDF: boolean;
};

/**
 * Namespaced raster ids. The shared MacronutrientBalancePage uses the global
 * "main"/"result", which would collide if a future page order rendered both.
 */
const RASTER_SRC_ID = "wl-macro-pair-src";
const RASTER_OUT_ID = "wl-macro-pair-out";

/**
 * Macronutrient names come from the dictionary rather than GRAPH[].TITLE, which the
 * backend serves in English only. The payload's INDEX is the language-independent
 * identifier; an unrecognised index falls back to the payload title so a new
 * entry renders as delivered instead of being mislabelled.
 */
const MACRO_NAME_BY_INDEX: Record<number, keyof AyurmaStrings["macros"]["names"]> =
  {
    1: "protein",
    2: "carbs",
    3: "fat",
  };

const toMacroNodes = (
  graph: any[],
  lang: ReportLang,
  macros: AyurmaStrings["macros"]
): MacroNode[] =>
  [...(graph ?? [])]
    .sort((a, b) => (a.INDEX ?? 0) - (b.INDEX ?? 0))
    .map((entry, i) => {
      const nameKey = MACRO_NAME_BY_INDEX[entry.INDEX];
      return {
        key: String(entry.INDEX ?? i),
        name: nameKey ? macros.names[nameKey] : entry.TITLE,
        value: parseAmount(entry.PERCENT) ?? 0,
        // "%" is language-neutral; kcal and g are re-attached from the dictionary.
        percent: entry.PERCENT,
        grams: formatAmount(entry.G, lang, macros.units.g),
        kcal: formatAmount(entry.KCAL, lang, macros.units.kcal),
      };
    });

const CaloricNutritionPage = ({
  data,
  theme,
  lang,
  t,
  pageNumber,
  svgConverter,
}: Props): JSX.Element => {
  const caloric = data?.PAGES?.find(
    (page: any) => page.PAGE_TYPE === "CALORIC_BALANCE"
  );
  const macro = data?.PAGES?.find(
    (page: any) => page.PAGE_TYPE === "MACRO_BALANCE"
  );

  const section2 = caloric?.SECTION2 ?? {};
  const sections: any[] = macro?.SECTIONS ?? [];

  // Both triangles rasterize in a single pass. Doing them separately would let
  // one unmount while the other is still being read, and keeps the two live
  // Recharts trees on the page longer than necessary.
  //
  // Keyed on the language: the triangles carry localized captions, macro names
  // and units, so the bitmap has to be redrawn when the reader switches language.
  const rasterized = useRasterizedBlock({
    svgConverter,
    srcId: RASTER_SRC_ID,
    outId: RASTER_OUT_ID,
    width: 738,
    enabled: sections.length > 0,
    redrawKey: lang,
  });

  return (
    <WhiteLabelPage
      theme={theme}
      lang={lang}
      brandLabelTemplate={t.footer.brandLabel}
      sectionName={t.sections.caloricNutrition}
      pageNumber={pageNumber}
      dataAgentTarget="wl-caloric-nutrition"
    >
      <div className={classes.sectionLabel}>{t.caloric.heading}</div>

      <div className={classes.statCards}>
        <FlatStatCard
          title={t.caloric.burnTitle}
          color={theme.colors.accentA}
          items={[
            {
              value: formatAmount(section2.YOU_BURN_DAYS_YOU_WORKOUT, lang),
              label: t.caloric.trainingDays,
            },
            {
              value: formatAmount(section2.YOU_BURN_DAYS_YOU_DONT_WORKOUT, lang),
              label: t.caloric.routineDays,
            },
          ]}
        />
        <FlatStatCard
          title={t.caloric.eatTitle}
          color={theme.colors.accentB}
          items={[
            {
              value: formatAmount(section2.YOU_SHOULD_EAT_DAYS_YOU_WORKOUT, lang),
              label: t.caloric.trainingDays,
            },
            {
              value: formatAmount(
                section2.YOU_SHOULD_EAT_DAYS_YOU_DONT_WORKOUT,
                lang
              ),
              label: t.caloric.routineDays,
            },
          ]}
        />
      </div>

      <div className={classes.sectionLabel}>{t.macros.heading}</div>

      <WhiteLabelCard padding="12px 16px">
        {/* Live charts live inside the output container and are swapped for the
            rasterized <img> once html2canvas has read them — the same pattern the
            default report's chart pages use, since html2canvas cannot be relied
            on to draw inline SVG during PDF export. */}
        <div id={RASTER_OUT_ID}>
          {!rasterized && (
            <div id={RASTER_SRC_ID} className={classes.triangles}>
              {sections.slice(0, 2).map((section, index) => (
                <MacroTriangle
                  key={section.INDEX ?? index}
                  caption={
                    index === 0 ? t.macros.trainingDays : t.macros.routineDays
                  }
                  nodes={toMacroNodes(section.GRAPH, lang, t.macros)}
                />
              ))}
            </div>
          )}
        </div>
      </WhiteLabelCard>

      <div className={classes.sectionLabel}>{t.recommendations.heading}</div>

      <div className={classes.recommendations}>
        <WhiteLabelCard>
          <LabeledFillRows
            rowHeight={40}
            labels={[
              t.recommendations.rows.mealFrequency,
              t.recommendations.rows.timing,
              t.recommendations.rows.hydration,
              t.recommendations.rows.infusions,
              t.recommendations.rows.aroma,
            ]}
          />
        </WhiteLabelCard>
      </div>
    </WhiteLabelPage>
  );
};

export default CaloricNutritionPage;
